import type { Ref } from 'react';
import { NavLink } from 'react-router-dom';
import { ChevronDown } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { motion, useReducedMotion } from 'motion/react';

import { cn } from '../../lib/cn';
import { SECTION_TONE } from './nav-config';
import { cheDoanNhan } from './nav-search';

/**
 * Nhãn có tô sáng phần khớp từ khoá.
 *
 * Không có từ khoá thì trả về đúng chuỗi, không bọc thêm thẻ nào — đường đi
 * thường ngày không phải trả giá cho tính năng tìm kiếm.
 *
 * Tô bằng `bg-primary-100` chứ không `<mark>`: `<mark>` mang màu vàng mặc định
 * của trình duyệt, nằm ngoài hệ token nên sai ở nền tối.
 */
function NhanTo({ nhan, tuKhoa }: { nhan: string; tuKhoa: readonly string[] }) {
  if (tuKhoa.length === 0) return <>{nhan}</>;
  return (
    <>
      {cheDoanNhan(nhan, tuKhoa).map((doan, i) =>
        doan.khop ? (
          <span key={i} className="rounded-xs bg-primary-100 text-primary-700">
            {doan.chu}
          </span>
        ) : (
          <span key={i}>{doan.chu}</span>
        ),
      )}
    </>
  );
}

/**
 * `layoutId` dùng chung cho viên thuốc đánh dấu mục đang chọn.
 *
 * Mọi mục lá của thanh điều hướng dùng **cùng một** id, nên khi mục đang chọn
 * đổi chỗ, Motion nhận ra đó vẫn là một phần tử và cho nó **trượt** từ mục cũ
 * sang mục mới thay vì biến mất rồi hiện lại.
 *
 * Đặt tiền tố `sidebar-` để không đụng `layoutId` của bất kỳ nhóm nào khác
 * thêm sau này.
 */
const LAYOUT_ID_VIEN_THUOC = 'sidebar-vien-thuoc-dang-chon';

/**
 * Viên thuốc đánh dấu mục đang chọn (§4.3).
 *
 * Trượt từ mục cũ sang mục mới bằng `layoutId` của `motion` — đúng như bản gốc
 * (ADR-UI-0008). Trước đây kho này vẽ viên thuốc tĩnh để bớt một thư viện; nay đã
 * có `motion` nên khôi phục hiệu ứng.
 */
function ActiveIndicator({ collapsed }: { collapsed: boolean }) {
  const giamChuyenDong = useReducedMotion();

  /*
   * Giảm chuyển động: vẽ thẳng, không `layoutId`. Nếu vẫn truyền `layoutId`
   * thì Motion vẫn chạy animation trượt — khối `@media` trong CSS không chặn
   * được vì Motion ghi inline style bằng JS.
   */
  if (giamChuyenDong) {
    return (
      <span aria-hidden className="absolute inset-0 rounded-lg bg-primary-50">
        {!collapsed && (
          <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-primary-600" />
        )}
      </span>
    );
  }

  return (
    <motion.span
      aria-hidden
      layoutId={LAYOUT_ID_VIEN_THUOC}
      className="absolute inset-0 bg-primary-50"
      /*
       * `borderRadius` đặt qua `style`, **không** qua class: Motion chỉ bù méo
       * được bo góc khi nó đọc được giá trị, nếu để Tailwind lo thì góc bị kéo
       * dãn trong lúc trượt.
       */
      style={{ borderRadius: 'var(--radius-lg)' }}
      transition={{ type: 'spring', stiffness: 420, damping: 34, mass: 0.7 }}
    >
      {/* Vạch nhấn bên trái; bỏ khi thanh đang gập. */}
      {!collapsed && (
        <span className="absolute inset-y-1.5 left-0 w-0.5 rounded-full bg-primary-600" />
      )}
    </motion.span>
  );
}

interface LeafItemProps {
  label: string;
  href: string;
  icon: LucideIcon;
  collapsed: boolean;
  /** Mục con thì thụt sâu hơn và biểu tượng nhỏ hơn. */
  isChild?: boolean;
  matchPrefix?: boolean;
  /** Từ khoá đã chuẩn hoá, để tô sáng phần khớp. */
  tuKhoa?: readonly string[];
  /** Đang được trỏ tới bằng ↑ ↓ (chưa mở). */
  dangTro?: boolean;
  /** Chỉ gắn cho mục **đang trỏ**, để thanh cuộn kéo nó vào tầm mắt. */
  refMuc?: Ref<HTMLAnchorElement>;
  /**
   * Tên nhóm, chỉ truyền khi **đang tìm và nhãn này bị trùng**.
   *
   * Gõ `tong quan` cho ra năm dòng đều tên `Tổng quan` — của Trợ lí AI, Thẩm
   * định, eKYC, Báo cáo, Nhật ký. Không có gì phân biệt thì người dùng phải
   * bấm thử từng dòng. Tên nhóm là thứ ngắn nhất nói đủ.
   */
  nhomCha?: string;
}

/** Mục lá: một trang thật sự. */
export function SidebarLeafItem({
  label,
  href,
  icon: Icon,
  collapsed,
  isChild = false,
  matchPrefix = false,
  tuKhoa = [],
  dangTro = false,
  nhomCha,
  refMuc,
}: LeafItemProps) {
  return (
    // Lề nằm ở <div> bọc ngoài, không đặt bằng `mx-*` trên chính mục. Nhờ vậy
    // mục luôn `w-full` trong phần còn lại và `justify-center` căn giữa đúng ở
    // cả <a> lẫn <button> — xem ghi chú ở `SidebarParentItem`.
    <div className={collapsed ? 'px-1.5' : 'px-2'}>
      <NavLink
        ref={refMuc}
        to={href}
        // `end` tắt khi matchPrefix: mục `Nhận dạng OCR` cũng sáng cho route con
        // `/xu-ly/nhan-dang/[schemaCode]` vì route đó không có mục nav riêng.
        end={!matchPrefix}
        // Nhãn cho trình đọc màn hình khi thanh gập: lúc đó `label` không được
        // dựng ra DOM nên nút chỉ còn biểu tượng, `title` một mình không đủ
        // (§15: nút chỉ có biểu tượng cần `aria-label` BÊN CẠNH `title`).
        aria-label={collapsed ? label : undefined}
        title={collapsed ? label : undefined}
        className={({ isActive }) =>
          cn(
            'group relative flex w-full items-center rounded-lg transition-colors',
            collapsed
              ? 'justify-center py-2'
              : cn('gap-3 py-2 text-sm', isChild ? 'pl-7 pr-3' : 'px-3'),
            isActive
              ? 'font-semibold text-primary-700'
              : 'text-content-secondary hover:bg-subtle hover:text-content-primary',
            // Mục đang trỏ bằng bàn phím: viền thay vì nền, để không bị nhầm
            // với mục đang mở (nền `bg-primary-50`).
            dangTro && 'ring-1 ring-inset ring-primary-300',
          )
        }
      >
        {({ isActive }) => (
          <>
            {isActive && <ActiveIndicator collapsed={collapsed} />}
            <Icon
              size={collapsed ? 18 : isChild ? 15 : 17}
              className={cn('relative z-10 shrink-0', isActive ? 'text-primary-600' : SECTION_TONE)}
            />
            {!collapsed && (
              // `min-w-0` là bắt buộc để `truncate` ở con có chỗ co lại; thiếu
              // nó thì nhãn dài đẩy tên nhóm ra ngoài thanh.
              <span className="sidebar-reveal relative z-10 min-w-0 flex-1">
                <span className="block truncate">
                  <NhanTo nhan={label} tuKhoa={tuKhoa} />
                </span>
                {nhomCha && (
                  <span className="block truncate text-2xs font-normal text-content-muted">
                    {nhomCha}
                  </span>
                )}
              </span>
            )}
          </>
        )}
      </NavLink>
    </div>
  );
}

interface ParentItemProps {
  label: string;
  icon: LucideIcon;
  collapsed: boolean;
  open: boolean;
  /** Có mục con nào đang là route hiện tại. */
  hasActiveChild: boolean;
  onToggle: () => void;
  /** Từ khoá đã chuẩn hoá, để tô sáng phần khớp. */
  tuKhoa?: readonly string[];
}

/**
 * Mục cha: **chỉ** là công tắc gập, không bao giờ là một trang.
 *
 * Khi một mục con đang mở, mục cha được in đậm nhưng **không** đổi sang màu
 * primary — chỉ mục con mới nhận viên thuốc màu, để mắt biết đâu là trang đang
 * xem.
 */
export function SidebarParentItem({
  label,
  icon: Icon,
  collapsed,
  open,
  hasActiveChild,
  onToggle,
  tuKhoa = [],
}: ParentItemProps) {
  return (
    <div className={collapsed ? 'px-1.5' : 'px-2'}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        // Khi gập, nhãn không được dựng ra DOM nên nút chỉ còn biểu tượng:
        // cần `aria-label` bên cạnh `title` (§15).
        aria-label={collapsed ? label : undefined}
        title={collapsed ? label : undefined}
        className={cn(
          // `w-full` là bắt buộc với <button>: trình duyệt cho nút mặc định
          // `width: fit-content`, nên khác <a>, nó **không** tự giãn dù đã là
          // block-level flex container. Thiếu nó thì `justify-center` vô nghĩa
          // và biểu tượng của mục cha dính sát mép trái khi gập.
          'group relative flex w-full items-center rounded-lg transition-colors',
          collapsed ? 'justify-center py-2' : 'gap-3 px-3 py-2 text-sm',
          hasActiveChild
            ? 'font-semibold text-content-primary'
            : 'text-content-secondary hover:bg-subtle hover:text-content-primary',
        )}
      >
        <Icon size={collapsed ? 18 : 17} className={cn('relative z-10 shrink-0', SECTION_TONE)} />
        {!collapsed && (
          <>
            <span className="sidebar-reveal relative z-10 flex-1 truncate text-left">
              <NhanTo nhan={label} tuKhoa={tuKhoa} />
            </span>
            <ChevronDown
              size={13}
              className={cn(
                'shrink-0 text-content-muted transition-transform duration-200',
                open && 'rotate-180',
              )}
            />
          </>
        )}
      </button>
    </div>
  );
}
